<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import MetricCard from '../overview/MetricCard.vue';
import BaseHeatmap from './BaseHeatmap.vue';
import HeatmapDateRangeSelector from './HeatmapDateRangeSelector.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useSectorlessAccount } from 'dashboard/composables/useSectorlessAccount';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

const props = defineProps({
  metric: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  downloadTitle: {
    type: String,
    required: true,
  },
  storeGetter: {
    type: String,
    required: true,
  },
  storeAction: {
    type: String,
    required: true,
  },
  downloadAction: {
    type: String,
    default: '',
  },
  uiFlagKey: {
    type: String,
    required: true,
  },
  colorScheme: {
    type: String,
    default: 'blue',
  },
});

const store = useStore();
const { isSectorlessAccount } = useSectorlessAccount();
const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const heatmapData = useMapGetter(props.storeGetter);
const sectors = useMapGetter('sectors/getSectors');
const currentRole = useMapGetter('getCurrentRole');
const currentSectorRole = useMapGetter('getCurrentSectorRole');
const currentAccount = useMapGetter('getCurrentAccount');

const selectedFrom = ref(null);
const selectedTo = ref(null);
const selectedDaysBefore = ref(null);
const selectedSector = ref(null);
const isMonthFilter = ref(false);
const currentMonthOffset = ref(0);

const selectedRange = computed(() => {
  if (!selectedFrom.value || !selectedTo.value) {
    return null;
  }
  return {
    from: selectedFrom.value,
    to: selectedTo.value,
  };
});

const isAccountAdmin = computed(() =>
  ['administrator', 'super_admin'].includes(currentRole.value)
);

const userSectorIds = computed(() => {
  const memberships = currentAccount.value?.sector_memberships || [];
  return memberships
    .filter(membership => membership.role === 'sector_admin')
    .map(membership => membership.sector_id);
});

const numberOfRows = computed(() => {
  if (!selectedRange.value) {
    return 0;
  }
  const dateDifference = differenceInCalendarDays(
    selectedRange.value.to,
    selectedRange.value.from
  );
  return dateDifference + 1;
});

const sectorMenuItems = computed(() => {
  if (!isAccountAdmin.value) {
    return sectors.value
      .filter(sector => userSectorIds.value.includes(sector.id))
      .map(sector => ({
        label: sector.name,
        value: sector.id,
        action: 'select_sector',
      }));
  }

  return [
    {
      label: t('OVERVIEW_REPORTS.ALL_SECTORS'),
      value: null,
      action: 'select_sector',
    },
    ...sectors.value.map(sector => ({
      label: sector.name,
      value: sector.id,
      action: 'select_sector',
    })),
  ];
});

const selectedSectorFilter = computed(() => {
  if (!selectedSector.value) {
    if (isAccountAdmin.value) {
      return { label: t('OVERVIEW_REPORTS.ALL_SECTORS') };
    }

    return { label: sectorMenuItems.value[0]?.label || '' };
  }

  return sectorMenuItems.value.find(
    item => item.value === selectedSector.value.id
  );
});

const isLoading = computed(() => uiFlags.value[props.uiFlagKey]);

// Keeps relative presets (last 7 days / this month) aligned with "now" during live refreshes.
const resolveActiveRange = () => {
  if (isMonthFilter.value && currentMonthOffset.value === 0) {
    const now = new Date();
    const monthStart = startOfMonth(now);
    return {
      from: startOfDay(monthStart),
      to: endOfDay(now),
    };
  }

  if (!isMonthFilter.value && selectedDaysBefore.value !== null) {
    const to = endOfDay(new Date());
    return {
      from: startOfDay(subDays(to, Number(selectedDaysBefore.value))),
      to,
    };
  }

  return selectedRange.value;
};

const downloadHeatmapData = () => {
  const range = resolveActiveRange();
  if (!range) {
    return;
  }

  const { to } = range;
  const shouldUseBackendDownload =
    !isMonthFilter.value && !selectedSector.value && props.downloadAction;

  if (shouldUseBackendDownload) {
    store.dispatch(props.downloadAction, {
      daysBefore: selectedDaysBefore.value,
      to: getUnixTime(to),
    });
    return;
  }

  // Generate CSV from store data
  if (!heatmapData.value || heatmapData.value.length === 0) {
    return;
  }

  // Create CSV headers
  const headers = ['Date', 'Hour', props.title];
  const rows = [headers];

  // Convert heatmap data to rows
  heatmapData.value.forEach(item => {
    const date = new Date(item.timestamp * 1000);
    const dateStr = format(date, 'yyyy-MM-dd');
    const hour = date.getHours();
    rows.push([dateStr, `${hour}:00 - ${hour + 1}:00`, item.value]);
  });

  // Convert to CSV string
  const csvContent = rows.map(row => row.join(',')).join('\n');

  // Generate filename
  const sectorSuffix = selectedSector.value
    ? `_${selectedSector.value.name.replace(/[^a-z0-9]/gi, '_')}`
    : '';
  const fileName = `${props.downloadTitle}${sectorSuffix}_${format(
    new Date(),
    'dd-MM-yyyy'
  )}.csv`;

  // Download the file
  downloadCsvFile(fileName, csvContent);
};

const [showSectorDropdown, toggleSectorDropdown] = useToggle();

const fetchHeatmapData = () => {
  if (isLoading.value) {
    return;
  }

  const range = resolveActiveRange();
  if (!range) {
    return;
  }

  const { from, to } = range;

  const params = {
    metric: props.metric,
    from: getUnixTime(from),
    to: getUnixTime(to),
    groupBy: 'hour',
    businessHours: false,
  };

  if (selectedSector.value) {
    params.type = 'sector';
    params.id = selectedSector.value.id;
  }

  store.dispatch(props.storeAction, params);
};

const handleSectorAction = ({ value }) => {
  toggleSectorDropdown(false);
  selectedSector.value = value
    ? sectors.value.find(sector => sector.id === value)
    : null;
};

const { startRefetching } = useLiveRefresh(fetchHeatmapData);

const handleRangeTypeChange = type => {
  isMonthFilter.value = type === 'month';
};

const handleMonthOffsetChange = offset => {
  currentMonthOffset.value = offset;
};

watch(
  () => [selectedFrom.value, selectedTo.value],
  ([from, to]) => {
    if (from && to) {
      fetchHeatmapData();
    }
  }
);

watch(
  () => selectedSector.value,
  () => {
    if (selectedRange.value) {
      fetchHeatmapData();
    }
  }
);

watch(
  () => sectorMenuItems.value,
  items => {
    if (!items.length) {
      return;
    }

    if (!isAccountAdmin.value && currentSectorRole.value === 'sector_admin') {
      selectedSector.value = sectors.value.find(
        sector => sector.id === items[0].value
      );
      return;
    }

    if (!selectedSector.value && isAccountAdmin.value) {
      selectedSector.value = null;
    }
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('sectors/get');
  startRefetching();
});
</script>

<template>
  <div class="flex flex-row flex-wrap max-w-full">
    <MetricCard :header="title">
      <template #control>
        <HeatmapDateRangeSelector
          v-model:from="selectedFrom"
          v-model:to="selectedTo"
          v-model:days-num="selectedDaysBefore"
          @range-type-change="handleRangeTypeChange"
          @month-offset-change="handleMonthOffsetChange"
        />
        <div
          v-if="!isSectorlessAccount && isAccountAdmin"
          v-on-clickaway="() => toggleSectorDropdown(false)"
          class="relative flex items-center group"
        >
          <Button
            sm
            slate
            faded
            :label="selectedSectorFilter.label"
            class="rounded-md group-hover:bg-n-alpha-2 max-w-[200px]"
            @click="toggleSectorDropdown()"
          />
          <DropdownMenu
            v-if="showSectorDropdown"
            :menu-items="sectorMenuItems"
            show-search
            :search-placeholder="t('OVERVIEW_REPORTS.SEARCH_SECTOR')"
            class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full !min-w-56 max-w-56 max-h-96 overflow-y-auto"
            @action="handleSectorAction($event)"
          />
        </div>
        <div
          v-else-if="!isSectorlessAccount"
          class="relative flex items-center"
        >
          <Button
            sm
            slate
            faded
            :label="selectedSectorFilter.label"
            class="rounded-md max-w-[200px]"
            :disabled="true"
          />
        </div>
        <Button
          v-tooltip="t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.DOWNLOAD_REPORT')"
          sm
          slate
          faded
          icon="i-lucide-download"
          class="rounded-md group-hover:bg-n-alpha-2"
          @click="downloadHeatmapData"
        />
      </template>
      <BaseHeatmap
        :heatmap-data="heatmapData"
        :number-of-rows="numberOfRows"
        :is-loading="isLoading"
        :color-scheme="colorScheme"
      />
    </MetricCard>
  </div>
</template>
